import { AfterViewInit, OnDestroy, Component, Inject } from "@angular/core";
import {
    NmsWizardStepModel,
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import { ANGULARJS_TRANSLATE, LABEL_COLOR_SERVICE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Subscription } from "rxjs";
import {
    CircuitLinksFilterResources,
    DwdmCircuitPortData,
    DwdmCircuitSlotData,
    LinkInfoCircuit,
    LinksChangesCallbacks,
    LinksChangesToBeApplied,
    LinksStepModel
} from "./links-step.model";
import { ModalFactory } from "@nms-ng2/app/shared/services/modal/modal.factory";
import { SelectLinksModalComponent } from "@nms-ng2/app/modules/topology/links/select-links-modal/select-links-modal.component";
import { LinkInfo, STATUS } from "@nms-ng2/app/modules/topology/links/links.model";
import { ColumnConfigType, SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import { LinkInfoToLinkInfoCircuitConverterService } from "./services/link-to-link-info-circuit-converter.service";
import { EndsStepModel } from "../1-ends/ends-step.model";
import { IntermediatesStepModel } from "../3-intermediates/intermediates-step.model";
import { GenerateAlphabeticIdentifierService } from "@nms-ng2/app/modules/topology/utils/generate-alphabetic-identifier.service";
import { NmsTableValidatorService, PagingFilterType } from "@nms-angular-toolkit/nms-table";
import { PRIMARY_PRESSED_COLOR } from "@nms-ng2/app/shared/constants/colors";
import { CircuitValidationService } from "../../../services/circuit-validation.service";
import { DwdmCircuitWizardValidationUtilsService } from "../../../services/dwdm-circuit-wizard-validation-utils.service";
import { CircuitDiagram } from "../../../diagram/models/diagram.models";
import { TranspondersStepModel } from "../2-transponders/transponders-step.model";
import { DwdmDiagramWizardConverterService } from "../../diagram/dwdm-diagram-wizard-converter.service";
import { AmplifiersStepModel } from "../5-amplifiers/amplifiers-step.model";
import { OscManagementStepModel } from "../6-osc-management/osc-management-step.model";
import { GeneralStepModel } from "../7-general/general-step.model";
import { LinkRemovalEventService } from "./services/link-removal-event.service.component";
import { LinkFormatterService } from "./services/link-formatter.service";
import { DwdmCircuitWizardUtilsService } from "../../utils/dwdm-circuit-wizard-utils.service";

/**
 * Step Links do DwdmCircuitWizard.
 */
@Component({
    selector: "links-step",
    templateUrl: "./links-step.component.html",
    styleUrls: ["./links-step.component.scss"]
})
export class LinksStepComponent extends AbstractNmsWizardStep implements AfterViewInit, OnDestroy {
    model: LinksStepModel;
    endsModel: EndsStepModel;
    transpondersModel: TranspondersStepModel;
    intermediatesModel: IntermediatesStepModel;
    amplifiersModel: AmplifiersStepModel;
    oscsModel: OscManagementStepModel;
    generalModel: GeneralStepModel;

    subscriptions: Array<Subscription> = [];
    selectedLinks: LinkInfoCircuit[] = [];
    aEndSlotSelected: DwdmCircuitSlotData[] = [];
    zEndSlotSelected: DwdmCircuitSlotData[] = [];

    columns: SimpleTableColumnConfig[] = [];
    columnsSortable: Record<string, boolean> = {
        identifier: true,
        name: true,
        aEnd: true,
        zEnd: true,
        situation: true,
        status: true,
        attenuation: true,
        distance: true
    };

    circuitDiagram: CircuitDiagram;
    private identifiersInUse: Array<string> = [];

    /**
     * Propriedade que mantém a última modificação do usuário armazenada
     * para posterior aplicação na função @see processChanges.
     */
    lastChangeToBeApplied: LinksChangesToBeApplied = null;

    /**
     * Ações que devem ser realizadas para cada tipo de modificação que o usuário pode realizar neste step.
     */
    changesCallbacks: LinksChangesCallbacks = {
        addLinks: this.convertAndGenerateLinksIndentifier.bind(this),
        removeLink: this.removeLinkChange.bind(this),
        removeAllLinks: this.removeAllLinksChange.bind(this)
    };

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any, private readonly modalFactory: ModalFactory,
            @Inject(LABEL_COLOR_SERVICE) private readonly labelColorService: any,
            private readonly linkToLinkInfoCircuitConverter: LinkInfoToLinkInfoCircuitConverterService,
            private readonly linkFormatterService: LinkFormatterService,
            private readonly generateIdentifierService: GenerateAlphabeticIdentifierService,
            private readonly nmsTableValidatorService: NmsTableValidatorService,
            private readonly circuitValidationService: CircuitValidationService,
            private readonly circuitWizardValidationUtilsService: DwdmCircuitWizardValidationUtilsService,
            private readonly diagramConverterService: DwdmDiagramWizardConverterService,
            private readonly circuitWizardUtilsService: DwdmCircuitWizardUtilsService,
            private readonly linkRemovalEventService: LinkRemovalEventService) {
        super();

        this.columns = [
            {
                field: "identifier",
                label: this.translate.instant("table.links.identifier"),
                customColor: PRIMARY_PRESSED_COLOR,
                width: "5%"
            },
            {
                field: "name",
                label: this.translate.instant("table.links.name"),
                width: "25%",
                optionalColumn: {
                    type: ColumnConfigType.TEXT_TIPICON,
                    hasTooltip: true,
                    tooltipContent: (linkInfoCircuit: LinkInfoCircuit) => {
                        return linkInfoCircuit.description
                    }
                },
            },
            {
                field: "aEnd",
                label: this.translate.instant("table.links.aEnd"),
                width: "20%"
            },
            {
                field: "zEnd",
                label: this.translate.instant("table.links.zEnd"),
                width: "20%"
            },
            {
                field: "status",
                label: this.translate.instant("table.links.status"),
                optionalColumn: {
                    type: ColumnConfigType.STATUS,
                    convert: (status: string) => {
                        const { translationKey, bgColor } = STATUS[status.trim().toUpperCase()];
                        const color = this.labelColorService.getBestColorContrastForForeground(bgColor);
                        const label = this.translate.instant(translationKey);

                        return { label, bgColor, color };
                    }
                }
            },
            {
                field: "situation",
                label: this.translate.instant("table.links.situation"),
            },
            {
                field: "distance",
                label: this.translate.instant("table.links.distance"),
            },
            {
                field: "attenuation",
                label: this.translate.instant("table.links.attenuation"),

            },
            {
                field: "actions",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.actions"),
                width: "5%",
                optionalColumn: {
                    type: ColumnConfigType.ACTIONS,
                    actions: [
                        {
                            id: "remove",
                            iconName: "remove",
                            onAction: (row: LinkInfoCircuit) => {
                                this.nmsTableValidatorService.showConfirmOptions({
                                    title: this.translate.instant("dwdm.circuit.wizard.links.remove.confirm.title"),
                                    description: this.translate.instant("dwdm.circuit.wizard.links.remove.single.confirm")
                                }).then(() => {
                                    this.removeLink(row);
                                });
                            },
                            getTooltip: () => {
                                return this.translate.instant("buttons.deleteEntry");
                            }
                        }
                    ]
                },
            }
        ];
    }

    ngAfterViewInit() {
        const removeLinkSub = this.linkRemovalEventService.getEventObservable()
            .subscribe(linkToRemove => this.removeLink(linkToRemove as LinkInfoCircuit));
        this.model = new LinksStepModel();
        this.subscriptions.push(removeLinkSub);
    }

    ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    goingToPreviousStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    async comingFromNextStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const model: Promise<NmsWizardStepModel> = this.resolveModel();
        await this.updateDiagramModel(wizardStepsCacheMap);

        return model;
    }

    async comingFromPreviousStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {

        this.model = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel || new LinksStepModel();
        this.endsModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel;
        this.transpondersModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        this.amplifiersModel = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel;

        this.aEndSlotSelected = this.filterSlotsWithSelectedPorts(this.endsModel.aEndSlots, this.endsModel.aEndPorts);
        this.zEndSlotSelected = this.filterSlotsWithSelectedPorts(this.endsModel.zEndSlots, this.endsModel.zEndPorts);

        if (this.endsModel) {
            this.model.aEndResourceId = this.endsModel.aEndDevice.id;
            this.model.zEndResourceId = this.endsModel.zEndDevice.id;

            this.model.aEndUseMux = this.endsModel.aEndHasMux;
            this.model.zEndUseMux = this.endsModel.zEndHasMux;

            if (this.model.aEndUseMux) {
                this.model.aEndMuxResourceId = this.endsModel.aEndMux.resourceId;
            }

            if (this.model.zEndUseMux) {
                this.model.zEndMuxResourceId = this.endsModel.zEndMux.resourceId;
            }
        }

        if (this.model.links) {
            this.selectedLinks = this.model.links;
            this.generateLinksIdentifier(this.selectedLinks);
        }

        const intermediatesData = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        this.model.intermediatesResourceIds = intermediatesData.useIntermediateDevices
            ? intermediatesData?.devices.map(device => device.resourceId)
            : [];

        await this.updateDiagramModelWithCurrentValues();
        this.stepLoaded = true;

        return this.resolveModel();
    }

    async allowPrevious(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        this.resolveModel();
        const linksValidatorData = this.circuitWizardValidationUtilsService.buildLinksData(this.model);
        return this.circuitValidationService.validateLinks(linksValidatorData);
    }

    async allowFinish(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    validate(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(new NmsWizardStepValidationResponse(true));
    }

    resolveModel(): Promise<NmsWizardStepModel> {
        this.model.links = this.selectedLinks;
        return Promise.resolve(this.model);
    }

    /**
     * Abre modal com os possíveis Links de um Circuito, baseado nos
     * Ends e Intermediários.
     */
    async openSelectLinksModal() {
        const title = this.translate.instant("add.links.label");
        const circuitEquipmentsResourceIds: string[] = [
            this.model.aEndResourceId,
            this.model.zEndResourceId,
            ...this.model.intermediatesResourceIds
        ].map(id => id.toString());

        const filterResources: CircuitLinksFilterResources = {
            aEndResourceId: this.model.aEndResourceId,
            aEndMuxResourceId: this.model.aEndMuxResourceId,
            aEndSlots: this.aEndSlotSelected,
            zEndSlots: this.zEndSlotSelected,
            zEndResourceId: this.model.zEndResourceId,
            zEndMuxResourceId: this.model.zEndMuxResourceId,
            intermediatesResourceIds: this.model.intermediatesResourceIds
        };

        const links = await this.modalFactory.openSimpleModal(SelectLinksModalComponent, {
            pageTitle: title,
            alreadySelectedLinksIds: this.selectedLinks.map((link) => link.id),
            filterResources,
            filterParams: {
                aEndResourceId: {
                    pagingFilterType: PagingFilterType.INCLUDES,
                    filterValues: circuitEquipmentsResourceIds
                },
                zEndResourceId: {
                    pagingFilterType: PagingFilterType.INCLUDES,
                    filterValues: circuitEquipmentsResourceIds
                }
            },
            operatorType: "OR"
        });

        this.lastChangeToBeApplied = {
            change: "addLinks",
            values: links
        }

        if (links.length > 0) {
            this.emitChangesWereMade(this.getCurrentStepModel());
        }
    }

    removeAllSelectLinks() {
        this.nmsTableValidatorService.showConfirmOptions({
            title: this.translate.instant("dwdm.circuit.wizard.links.remove.confirm.title"),
            description: this.translate.instant("dwdm.circuit.wizard.links.remove.all.confirm"),
        }).then(() => {
            this.lastChangeToBeApplied = {
                change: "removeAllLinks"
            }
            this.emitChangesWereMade(this.getCurrentStepModel());
        });
    }

    async processChanges(cache: NmsWizardStepMapData): Promise<void> {
        if (this.lastChangeToBeApplied) {
            await this.changesCallbacks[this.lastChangeToBeApplied.change](this.lastChangeToBeApplied.values);
            this.lastChangeToBeApplied = null;
        }

        this.updateDiagramModel(cache);
        this.updateStepsModel(cache);
    }

    private async updateDiagramModel(cache: NmsWizardStepMapData): Promise<void> {
        await this.resolveModel();
        this.circuitDiagram = this.diagramConverterService.convert(cache, true);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private async updateDiagramModelWithCurrentValues(): Promise<void> {
        await this.resolveModel();
        const stepsModel: NmsWizardStepMapData = {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.model,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        };
        this.circuitDiagram = this.diagramConverterService.convert(stepsModel, true);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    /**
     * Realiza a atualização dos dados do step após um processamento de mudanças.
     * Quando ocorre invalidação dos steps posteriores, as propriedades que armazenam os
     * dados precisam refletir os novos valores.
     */
    private updateStepsModel(cache: NmsWizardStepMapData): Promise<void> {
        this.endsModel = cache["EndsStepModel"] as EndsStepModel;
        this.transpondersModel = cache["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = cache["IntermediatesStepModel"] as IntermediatesStepModel;
        this.model = cache["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = cache["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = cache["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = cache["GeneralStepModel"] as GeneralStepModel;

        return Promise.resolve();
    }

    /**
     * Retorna os dados atuais.
     * Como a cache @see NmsWizardStepMapData é atualizada apenas no botão próximo do diagrama,
     * é preciso pegar os dados atuais para garantir o funcionamento correto do processChanges.
     */
    private getCurrentStepModel(): NmsWizardStepMapData {
        return {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.model,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        } as NmsWizardStepMapData;
    }

    private convertAndGenerateLinksIndentifier(links: Array<LinkInfo>) {
        if (links.length > 0) {
            const newLinks = this.linkToLinkInfoCircuitConverter.convertLinks(links);
            const allSelectedLinks = [...this.selectedLinks, ...newLinks];

            this.linkFormatterService.formatLinks(newLinks);
            this.generateLinksIdentifier(allSelectedLinks);
        }
    }

    private generateLinksIdentifier(selectedLinks: Array<LinkInfoCircuit>) {
        selectedLinks.forEach((link) => {
            if (!link.identifier) {
                link.identifier = this.generateIdentifierService.generate("Link", this.identifiersInUse);
            }
        })
        this.selectedLinks = selectedLinks;
        this.selectedLinks.sort((link, linkToCompare) => link.identifier.localeCompare(linkToCompare.identifier));
    }

    private removeLink(linkInfoCircuit: LinkInfoCircuit) {
        this.lastChangeToBeApplied = {
            change: "removeLink",
            values: [linkInfoCircuit]
        }
        this.emitChangesWereMade(this.getCurrentStepModel());
    }

    private filterSlotsWithSelectedPorts(slots: DwdmCircuitSlotData[],
            selectedPorts: DwdmCircuitPortData[]): DwdmCircuitSlotData[] {
        return slots.map(slot => {
            const filteredPorts = slot.ports.filter(port =>
                selectedPorts.some(selected => this.isPortSelected(port, selected)));

            return {
                slotNo: slot.slotNo,
                chassisNo: slot.chassisNo,
                slotModel: slot.slotModel,
                slotName: slot.slotName,
                ports: filteredPorts
            };
        });
    }

    private isPortSelected(port: DwdmCircuitPortData, selectedPort: DwdmCircuitPortData): boolean {
        return port.portNo === selectedPort.portNo && port.portName === selectedPort.portName;
    }

    hasChanges(): boolean {
        return this.lastChangeToBeApplied != null;
    }

    getDependenciesStepsNames(): string[] {
        return [
            "AmplifiersStepModel",
            "OscManagementStepModel"
        ];
    }

    private removeLinkChange(values: any) {
        const linkInfoCircuit = values[0];
        const linksToKeep = this.selectedLinks.filter((link) => linkInfoCircuit.id !== link.id);
        this.selectedLinks = linksToKeep;
        this.identifiersInUse = this.identifiersInUse.filter(i => i !== linkInfoCircuit.identifier);
    }

    private removeAllLinksChange() {
        this.selectedLinks = [];
        this.identifiersInUse = [];
    }
}
